<script lang="ts" setup>
import OmniDialog from "@/components/OmniDialog.vue";
//@ts-ignore
import OmniTextarea from "@/components/inputs/Textarea.vue";
//@ts-ignore
import EmailsField from "@/components/inputs/EmailsField.vue";
import { DialogSize } from "@/components/types";
import { computed, nextTick, onMounted, ref } from "vue";
import type { VForm } from "vuetify/components";
import type {
  CreateNewUpdateRequest,
  PreviewUpdateRequest,
  PreviewUpdateResponse,
  TripUpdate,
  TripUpdatePreview,
} from "@/api/trips/dto/update";
import { useSnackbarStore } from "@/store/snackbar.store";
import { API } from "@/api/api";
import { GoogleMap } from "vue3-google-map";
import GoogleMapsMixin from "@/mixins/google-maps.mixin";
import FileUploadingGrid from "@/components/FileUploadingGrid.vue";
import AlertMessage from "@/components/AlertMessage.vue";
import { CheckCallType } from "@/api/trips/dto/check-call";
import { useI18n } from "vue-i18n";
import ViewUpdate from "@/views/trips/components/ViewUpdate.vue";
import { useValidation } from "@/composables/useValidation";
import type { Recipient } from "@/types/recipients";
import { useBaseView } from "@/composables";

const api = new API();
const { t } = useI18n();

const { getApiKey } = GoogleMapsMixin.methods;

const { showSnackbarError } = useSnackbarStore();
const { requiredValidator, arrayRequiredValidator, notFoundedRecipientsValidator } = useValidation();
const { getPreviewPath, scrollToError } = useBaseView();

const showDialog = defineModel<boolean>({ default: false });
const props = defineProps<{
  travelOrderId: number;
  checkCallId: number;
  routeId: number;
  checkCallType: CheckCallType;
}>();

const emits = defineEmits<{
  (e: "success", update: TripUpdate, travelOrderId: number): void;
  (e: "update:modelValue", value: boolean): void;
}>();

const formRef = ref<VForm | null>(null);
const recipientsRef = ref<any>(null);
const loading = ref<boolean>(false);
const mapCenter = ref();
const fields = ref<Omit<PreviewUpdateResponse, "messageIdError">>({
  subject: "",
  message: "",
  messageId: "",
  recipients: [],
  files: [],
  failedRecipients: [],
});
const messageIdErrorRef = ref<string>();
const selectedFiles = ref<string[]>([]);
const initialRecipients = ref<string[] | null>(null);
const initialFailedRecipients = ref<string[] | null>(null);
const isErrorPreparedUpdate = ref<boolean>(false);
const showRecipientsMessage = ref<boolean>();
const isUpdatingRecipients = ref<boolean>();
const recipients = ref<Recipient[]>([]);
const isLoaded = ref<boolean>(false);
const isPreview = ref<boolean>(false);
const showFilesValidationDialog = ref<boolean>(false);

const title = computed(() => {
  if (isPreview.value) {
    return `${t("updates.broker-update-preview-for")} Travel order#${props.travelOrderId}`;
  }

  return `${t("updates.create-update-for")} Travel order#${props.travelOrderId}`;
});

const isMapVisible = computed(() => {
  return false;
});

const updatePreview = computed<TripUpdatePreview>(() => {
  return {
    message: fields.value.message,
    subject: fields.value.subject,
    emailRecipients: fields.value.recipients,
    files: fields.value.files.filter((file) => selectedFiles.value.includes(file.id)),
  };
});

const primaryButtonLabel = computed(() => {
  if (isPreview.value) {
    return t("general.create");
  }

  return t("general.preview");
});

const secondaryButtonLabel = computed(() => {
  if (isPreview.value) {
    return t("updates.edit-update");
  }

  return "";
});

async function create() {
  if (!props.routeId || !props.travelOrderId) {
    return;
  }

  const data: CreateNewUpdateRequest = {
    checkCallId: props.checkCallId,
    travelOrderId: props.travelOrderId,
    subject: fields.value.subject,
    messageId: fields.value.messageId,
    message: fields.value.message,
    recipients: fields.value.recipients,
    files: selectedFiles.value,
  };

  loading.value = true;
  try {
    const response = await api.events.createUpdate(props.routeId, data);
    emits("update:modelValue", false);

    if (response.data) {
      emits("success", response.data, props.travelOrderId);
    }
  } catch (e) {
    showSnackbarError("Could not create new update");
  } finally {
    loading.value = false;
  }
}

async function showPreview(confirm = false) {
  showFilesValidationDialog.value = false;

  const result = await formRef.value?.validate();

  if (!result?.valid) {
    scrollToError();

    return;
  }

  if (fields.value.files.length && !selectedFiles.value.length && !confirm) {
    showFilesValidationDialog.value = true;

    return;
  }

  isPreview.value = true;
}

function editUpdate() {
  isPreview.value = false;
}

async function onPrimaryClick() {
  if (isPreview.value) {
    await create();

    return;
  }
  const result = await formRef.value?.validate();

  if (!result?.valid) {
    scrollToError();
    return;
  }

  await showPreview();
}

function checkIsRecipientsChanged() {
  const initial = [...(initialRecipients.value || []), ...(initialFailedRecipients.value || [])].slice().sort();
  const current = (recipients.value.flatMap((recipient) => recipient.value) || []).slice().sort();

  if (initial.length !== current.length) {
    return true;
  }

  return !initial.every((recipient, index) => recipient === current[index]);
}

function setDefaultRecipients() {
  fields.value.recipients.forEach((email) => {
    const isNew = isLoaded.value && !initialRecipients.value?.includes(email);

    const recipient: Recipient = {
      value: email,
      class: isNew ? "border-success bg-green-lighten-5" : "",
    };

    recipients.value.push(recipient);
  });

  fields.value.failedRecipients.forEach((failedRecipient) => {
    const recipient: Recipient = {
      value: failedRecipient,
      class: "border-red-lighten-5 bg-red-lighten-5",
      notFounded: true,
    };

    recipients.value.push(recipient);
  });
}

async function validateRecipientsFields() {
  await nextTick();
  recipientsRef?.value.$refs?.emailsField?.validate();
}

async function getPreviewInfo() {
  if (isLoaded.value) {
    isUpdatingRecipients.value = true;
  }

  try {
    const request: PreviewUpdateRequest = {
      checkCallId: props.checkCallId,
      travelOrderId: props.travelOrderId,
      messageId: fields.value.messageId,
    };

    const response = await api.events.previewUpdate(props.routeId, request);

    if (response.success && response.data) {
      const { messageIdError, ...rest } = response.data;

      fields.value = {
        ...rest,
        files: rest.files.map((file) => {
          return {
            ...file,
            previewPath: getPreviewPath(file),
          };
        }),
      };

      setDefaultRecipients();

      if (!initialRecipients.value) {
        initialRecipients.value = fields.value.recipients;
      }

      if (!initialFailedRecipients.value) {
        initialFailedRecipients.value = fields.value.failedRecipients;
      }

      messageIdErrorRef.value = messageIdError;
      showRecipientsMessage.value = checkIsRecipientsChanged();
    } else {
      isErrorPreparedUpdate.value = true;
    }
  } catch (error) {
    isErrorPreparedUpdate.value = true;
    showSnackbarError("Could not load update preview");
  } finally {
    isLoaded.value = true;
    isUpdatingRecipients.value = false;
    await validateRecipientsFields();
  }
}

function setFile(fileId: string) {
  const index = selectedFiles.value.indexOf(fileId);

  if (index !== -1) {
    selectedFiles.value.splice(index, 1);
  } else {
    selectedFiles.value.push(fileId);
  }
}

function updateRecipients(newRecipients: Recipient[]) {
  recipients.value = newRecipients.map((recipient) => {
    if (initialFailedRecipients.value?.includes(recipient.value)) {
      return {
        ...recipient,
        class: "border-red-lighten-5 bg-red-lighten-5",
        notFounded: true,
      };
    }

    if (recipient.notFounded) {
      return {
        ...recipient,
        notFounded: false,
        class: "",
      };
    }

    return recipient;
  });

  fields.value.recipients = recipients.value.flatMap((recipient) => recipient.value);

  validateRecipientsFields();
}

async function onMessageIdUpdate(value: string) {
  fields.value.messageId = value.trim();
}

onMounted(async () => {
  await getPreviewInfo();
});
</script>

<template>
  <omni-dialog
    v-model="showDialog"
    :loading="loading"
    :primary-action-label="primaryButtonLabel"
    :primary-button-disabled="isErrorPreparedUpdate"
    :primary-button-loader="!isLoaded || isUpdatingRecipients"
    :secondary-action-label="secondaryButtonLabel"
    :size="DialogSize.xlarge"
    :title="title"
    @primary-action:click="onPrimaryClick"
    @secondary-action:click="editUpdate"
  >
    <transition mode="out-in">
      <v-form v-if="!isPreview" ref="formRef">
        <emails-field
          ref="recipientsRef"
          v-model="recipients"
          :disabled="isUpdatingRecipients || !isLoaded"
          :rules="[arrayRequiredValidator(recipients), notFoundedRecipientsValidator(recipients)]"
          data-qa="email-field"
          @update:model-value="updateRecipients"
        />

        <alert-message
          v-if="showRecipientsMessage && !isUpdatingRecipients"
          bg-color="green-lighten-5"
          border-color="success"
          class="mt-3"
          icon="mdi-check"
          icon-color="success"
        >
          The recipient list has been updated
        </alert-message>

        <alert-message
          v-if="isUpdatingRecipients"
          bg-color="green-lighten-5"
          border-color="success"
          class="mt-3"
          icon="mdi-loading"
          icon-class="recipients-spinner"
          icon-color="success"
        >
          Updating recipients…
        </alert-message>

        <v-text-field
          v-model="fields.messageId"
          :disabled="isUpdatingRecipients || !isLoaded"
          :error="Boolean(messageIdErrorRef)"
          :error-messages="messageIdErrorRef"
          class="mt-5"
          density="compact"
          label="Message ID"
          variant="outlined"
          @blur="getPreviewInfo"
          @update:model-value="onMessageIdUpdate"
        />

        <div v-if="messageIdErrorRef" class="mt-4"></div>

        <v-text-field
          v-model="fields.subject"
          :disabled="(Boolean(fields.messageId) && !Boolean(messageIdErrorRef)) || !isLoaded"
          :rules="[requiredValidator]"
          class="required"
          data-qa="subject-field"
          density="compact"
          label="Subject"
          variant="outlined"
        />

        <omni-textarea
          v-model="fields.message"
          :disabled="!isLoaded"
          :rules="[requiredValidator]"
          class="required mt-2"
          data-qa="update-modal-message"
          label="Message"
          rows="4"
          show-details
          variant="outlined"
        />

        <div v-if="fields.files.length" class="mb-6 mt-2">
          <div class="font-size-16 text-grey-darken-3 font-weight-500 mb-3">Files</div>

          <span class="text-grey-darken-2">{{ t("updates.select-files") }}</span>

          <file-uploading-grid v-model="fields.files" class="mt-4" disabled>
            <template #prefix-name="{ file }">
              <v-checkbox
                :model-value="selectedFiles.includes(file.id)"
                class="mr-3"
                color="primary"
                density="compact"
                hide-details
                @change="setFile(file.id)"
              ></v-checkbox>
            </template>
          </file-uploading-grid>
        </div>

        <google-map
          v-if="isMapVisible"
          :api-key="getApiKey()"
          :center="mapCenter"
          :fullscreen-control="false"
          :map-type-control="false"
          :street-view-control="false"
          :zoom="4"
          style="width: 100%; height: 450px"
        >
        </google-map>
      </v-form>

      <view-update v-else :check-call-type="props.checkCallType" :update="updatePreview" />
    </transition>

    <omni-dialog
      v-model="showFilesValidationDialog"
      :primary-action-label="t('general.attach-file')"
      :secondary-action-label="t('general.continue-without-attaching')"
      :title="t('updates.files-validation-error')"
      @primary-action:click="showFilesValidationDialog = false"
      @secondary-action:click="showPreview(true)"
    >
      <span class="text-grey-darken-1">{{ t("updates.confirm-create-without-files") }}</span>
    </omni-dialog>
  </omni-dialog>
</template>

<style lang="scss">
.recipients-spinner {
  animation: 1s infinite linear spin;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
